import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { pageHeroImages } from '../mock';
import { Check, Clock, Globe, Award, Sparkles, Phone, Mail, Download, Loader2, Star, MapPin, Calendar, Users, Wallet, Heart, Utensils, Plane, Hotel, Camera, Shield, ChevronRight, User, LogIn } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Step indicator component
const StepIndicator = ({ currentStep, steps }) => (
  <div className="step-indicator">
    {steps.map((step, index) => (
      <div key={index} className={`step ${index + 1 <= currentStep ? 'active' : ''} ${index + 1 < currentStep ? 'completed' : ''}`}>
        <div className="step-number">
          {index + 1 < currentStep ? <Check size={16} /> : index + 1}
        </div>
        <span className="step-label">{step}</span>
        {index < steps.length - 1 && <div className="step-line" />}
      </div>
    ))}
  </div>
);

// Value proposition cards
const ValueCard = ({ icon: Icon, title, description }) => (
  <div className="value-card">
    <div className="value-icon">
      <Icon size={24} />
    </div>
    <h4>{title}</h4>
    <p>{description}</p>
  </div>
);

const DesignMyJourney = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [requestId, setRequestId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [verifyLaterMode, setVerifyLaterMode] = useState(false);
  
  // OTP fields
  const [emailOtp, setEmailOtp] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [emailHint, setEmailHint] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  
  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    preferred_experience: '',
    destination: '',
    nights_per_destination: '', // For multi-city trips
    origin_city: '',
    travel_start_date: '',
    travel_end_date: '',
    num_travelers: 2,
    budget_range: '1-5L',
    travel_style: 'balanced',
    accommodation_type: 'mid-range',
    flight_class: 'economy',
    food_preference: 'both',
    special_interests: [],
    special_requests: '',
    request_callback: false,
    callback_time: ''
  });

  // Experiences list for dropdown
  const [experiences, setExperiences] = useState([]);

  // Generated itinerary
  const [itinerary, setItinerary] = useState(null);
  const [pollActive, setPollActive] = useState(false);

  // Fetch experiences on mount
  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const response = await axios.get(`${API}/experiences`);
        setExperiences(response.data);
      } catch (error) {
        console.error('Error fetching experiences:', error);
      }
    };
    fetchExperiences();
  }, []);
  
  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);
  
  // Polling effect for checking itinerary status
  useEffect(() => {
    if (!pollActive || !requestId) return;
    
    let attempts = 0;
    const maxAttempts = 40;
    let intervalId;
    
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/itinerary/status/${requestId}`);
        
        if (response.data.status === 'completed') {
          setPollActive(false);
          setItinerary(response.data.itinerary);
          setLoading(false);
          setCurrentStep(4); // Updated to step 4 (Thank You)
          clearInterval(intervalId);
          return;
        } else if (response.data.status === 'failed') {
          setPollActive(false);
          setLoading(false);
          setError(response.data.message || 'Failed to generate itinerary.');
          clearInterval(intervalId);
          return;
        }
        
        attempts++;
        if (attempts >= maxAttempts) {
          setPollActive(false);
          setLoading(false);
          setError('Generation taking too long. Please contact us.');
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error('Poll error:', err);
        attempts++;
        if (attempts >= maxAttempts) {
          setPollActive(false);
          setLoading(false);
          setError('Unable to check status.');
          clearInterval(intervalId);
        }
      }
    };
    
    // Start polling after 3 seconds, then every 5 seconds
    const timeoutId = setTimeout(() => {
      checkStatus();
      intervalId = setInterval(checkStatus, 5000);
    }, 3000);
    
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [pollActive, requestId]);

  const steps = ['Your Details', 'Verification', 'Trip Preferences', 'Confirmation'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      special_interests: prev.special_interests.includes(interest)
        ? prev.special_interests.filter(i => i !== interest)
        : [...prev.special_interests, interest]
    }));
  };

  // Step 1: Submit contact info and request OTP (Verify Now)
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/itinerary/request-otp`, formData);
      
      if (response.data.success) {
        setRequestId(response.data.request_id);
        setEmailHint(response.data.email_hint);
        setOtpSent(true);
        setResendTimer(60);
        setSuccessMessage('Verification codes sent to your email!');
        setVerifyLaterMode(false);
        setCurrentStep(2);
      } else {
        setError('Failed to send verification codes. Please try again.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || 'Failed to send verification codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 1 Alt: Verify Later — skip OTP, auto-create account, go to Step 3
  const handleVerifyLater = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/itinerary/verify-later`, formData);
      
      if (response.data.success) {
        setRequestId(response.data.request_id);
        setVerifyLaterMode(true);
        setSuccessMessage('Account created! Check your email for login details.');
        setCurrentStep(3); // Skip directly to trip details
      } else {
        setError('Failed to process request. Please try again.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || 'Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTPs
  const handleOtpVerify = async (e) => {
    e.preventDefault();
    if (!emailOtp || !mobileOtp) {
      setError('Please enter both verification codes');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/itinerary/verify-dual-otp?request_id=${requestId}&email_otp=${emailOtp}&phone_otp=${mobileOtp}`);
      
      if (response.data.success) {
        setSuccessMessage('Verification successful!');
        setCurrentStep(3); // Go to trip details
      } else {
        setError('Verification failed. Please check your codes.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/itinerary/resend-otp?request_id=${requestId}`);
      
      if (response.data.success) {
        setSuccessMessage('New verification codes sent to your email!');
        setResendTimer(60);
        setEmailOtp('');
        setMobileOtp('');
      }
    } catch (err) {
      setError('Failed to resend codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Submit trip details and generate itinerary
  const handleStep3Submit = async (e) => {
    e.preventDefault();
    if (!formData.destination || !formData.origin_city || !formData.travel_start_date || !formData.travel_end_date) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Send trip details including preferred_experience
      const tripDetails = {
        destination: formData.destination,
        origin_city: formData.origin_city,
        travel_start_date: formData.travel_start_date,
        travel_end_date: formData.travel_end_date,
        num_travelers: formData.num_travelers,
        budget_range: formData.budget_range,
        travel_style: formData.travel_style,
        accommodation_type: formData.accommodation_type,
        food_preference: formData.food_preference,
        special_interests: formData.special_interests,
        special_requests: formData.special_requests,
        preferred_experience: formData.preferred_experience || ""
      };
      
      // Update the lead with trip details and start generation
      const response = await axios.put(`${API}/itinerary/update-and-generate/${requestId}`, tripDetails, {
        timeout: 10000 // 10 second timeout
      });
      
      // Go to Thank You page immediately regardless of response
      setLoading(false);
      setCurrentStep(4);
      
    } catch (err) {
      console.error('Error:', err);
      // Even on error, go to Thank You page if we have a requestId
      // The generation might still be running in the background
      if (requestId) {
        setLoading(false);
        setCurrentStep(4);
      } else {
        setLoading(false);
        setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
      }
    }
  };

  const downloadPdf = () => {
    if (requestId) {
      window.open(`${API}/itinerary/download/${requestId}`, '_blank');
    }
  };

  // Reset form and go back to start
  const resetAndGoBack = () => {
    setCurrentStep(1);
    setItinerary(null);
    setRequestId(null);
    setPollActive(false);
    setOtpSent(false);
    setEmailOtp('');
    setMobileOtp('');
    setEmailHint('');
    setError('');
    setSuccessMessage('');
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      preferred_experience: '',
      destination: '',
      nights_per_destination: '',
      origin_city: '',
      travel_start_date: '',
      travel_end_date: '',
      num_travelers: 2,
      budget_range: '1-5L',
      travel_style: 'balanced',
      accommodation_type: 'mid-range',
      flight_class: 'economy',
      food_preference: 'both',
      special_interests: [],
      special_requests: '',
      request_callback: false,
      callback_time: ''
    });
    window.scrollTo(0, 0);
  };

  // Thank You Page - Show when generation is complete (Step 4)
  if (currentStep === 4) {
    return (
      <div className="page-container thank-you-page">
        <section className="thank-you-hero">
          <div className="thank-you-content">
            <div className="success-icon">
              <Check size={48} />
            </div>
            <h1>Thank You, {formData.first_name}!</h1>
            <p className="thank-you-subtitle">Your Signature Journey to <strong>{formData.destination}</strong> is Being Crafted</p>
            
            <div className="thank-you-message">
              <p>Our travel experts have received your itinerary request and will contact you shortly to discuss and finalize your dream journey.</p>
              <div className="contact-info-box">
                <p><strong>What happens next?</strong></p>
                <ul>
                  <li>Your account has been created — check your email ({formData.email}) for login details</li>
                  <li>Login to view and download your personalized itinerary</li>
                  <li>A JNT travel expert will call you within 24 hours</li>
                  <li>We'll discuss your preferences in detail</li>
                </ul>
              </div>
            </div>

            <div className="thank-you-actions">
              <Button onClick={() => navigate(`/client/login?email=${encodeURIComponent(formData.email)}`)} className="create-account-btn" data-testid="login-now-btn">
                <LogIn size={18} />
                Login to Your Account
              </Button>
              <Button onClick={resetAndGoBack} variant="outline" className="back-home-btn">
                Plan Another Journey
              </Button>
            </div>

            <div className="contact-directly">
              <p>Need immediate assistance?</p>
              <div className="quick-contact">
                <a href="tel:+919717841166" className="contact-link">
                  <Phone size={18} />
                  +91 97178 41166
                </a>
                <a href="https://wa.me/919717841166" target="_blank" rel="noopener noreferrer" className="contact-link whatsapp">
                  <Mail size={18} />
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-container signature-journey-page" data-testid="design-journey-page">
      {/* Main Form Section - At Top */}
      <section className="journey-form-section" style={{ paddingTop: '2rem' }}>
        <div className="container">
          <div className="form-wrapper">
            <div className="form-header">
              <h2>Create Your Signature Itinerary</h2>
              <p>Fill in your preferences and receive a personalized, JNT-crafted travel portfolio</p>
              <StepIndicator currentStep={currentStep} steps={steps} />
            </div>

            {error && (
              <div className="form-error-banner">
                <span>{error}</span>
                <button onClick={() => setError('')}>&times;</button>
              </div>
            )}

            {/* Step 1: Personal Details */}
            {currentStep === 1 && (
              <form onSubmit={handleStep1Submit} className="journey-form step-form" data-testid="step-1-form">
                <div className="form-section-title">
                  <Users size={20} />
                  <span>Personal Information</span>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your first name"
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your last name"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>

                <div className="form-callback">
                  <div className="checkbox-field">
                    <Checkbox 
                      id="request_callback"
                      checked={formData.request_callback}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, request_callback: checked }))}
                    />
                    <Label htmlFor="request_callback">I'd like a callback from a travel expert</Label>
                  </div>
                  {formData.request_callback && (
                    <div className="form-field callback-time">
                      <Label htmlFor="callback_time">Preferred callback time</Label>
                      <Select onValueChange={(value) => handleSelectChange('callback_time', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select time slot" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="morning">Morning (10 AM - 12 PM)</SelectItem>
                          <SelectItem value="afternoon">Afternoon (2 PM - 5 PM)</SelectItem>
                          <SelectItem value="evening">Evening (6 PM - 8 PM)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="form-actions dual-verify" style={{ display: 'flex', gap: '12px' }}>
                  <Button type="submit" className="form-next-btn" data-testid="verify-now-btn" disabled={loading} style={{ flex: 1 }}>
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Sending Verification...
                      </>
                    ) : (
                      <>
                        <Shield size={18} />
                        Verify Now
                      </>
                    )}
                  </Button>
                  <Button type="button" variant="outline" className="form-next-btn verify-later-btn" data-testid="verify-later-btn" disabled={loading} onClick={handleVerifyLater} style={{ flex: 1 }}>
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Clock size={18} />
                        Verify Later
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Step 2: OTP Verification */}
            {currentStep === 2 && (
              <form onSubmit={handleOtpVerify} className="journey-form step-form otp-form" data-testid="step-2-otp-form">
                <div className="form-section-title">
                  <Mail size={20} />
                  <span>Verify Your Contact</span>
                </div>

                <div className="otp-instruction">
                  <p>We've sent verification codes to <strong>{emailHint}</strong></p>
                  <p className="otp-note">Please check your email inbox (and spam folder) for both codes.</p>
                </div>

                {successMessage && (
                  <div className="form-success-banner">
                    <Check size={16} />
                    <span>{successMessage}</span>
                  </div>
                )}

                <div className="otp-inputs">
                  <div className="form-field">
                    <Label htmlFor="email_otp">Email Verification Code *</Label>
                    <Input
                      id="email_otp"
                      type="text"
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      className="otp-input"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="mobile_otp">Mobile Verification Code *</Label>
                    <Input
                      id="mobile_otp"
                      type="text"
                      value={mobileOtp}
                      onChange={(e) => setMobileOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      className="otp-input"
                      required
                    />
                  </div>
                </div>

                <div className="otp-timer">
                  {resendTimer > 0 ? (
                    <p>Resend codes in <strong>{resendTimer}s</strong></p>
                  ) : (
                    <button type="button" className="resend-btn" onClick={handleResendOtp} disabled={loading}>
                      Didn't receive codes? Resend
                    </button>
                  )}
                </div>

                <div className="form-actions">
                  <Button type="button" variant="outline" onClick={() => setCurrentStep(1)} className="form-back-btn">
                    Back
                  </Button>
                  <Button type="submit" className="form-next-btn" data-testid="verify-otp-btn" disabled={loading || emailOtp.length < 6 || mobileOtp.length < 6}>
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Verify & Continue
                        <ChevronRight size={18} />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Step 3: Trip Preferences */}
            {currentStep === 3 && (
              <form onSubmit={handleStep3Submit} className="journey-form step-form" data-testid="step-3-form">
                <div className="form-section-title">
                  <MapPin size={20} />
                  <span>Trip Details</span>
                </div>

                {/* Preferred Experience Dropdown */}
                <div className="form-row">
                  <div className="form-field full-width">
                    <Label htmlFor="preferred_experience">Preferred Experience</Label>
                    <Select 
                      value={formData.preferred_experience || "any"}
                      onValueChange={(value) => handleSelectChange('preferred_experience', value === "any" ? "" : value)}
                    >
                      <SelectTrigger id="preferred_experience">
                        <SelectValue placeholder="Select your preferred experience type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Experience</SelectItem>
                        {experiences.map((exp) => (
                          <SelectItem key={exp.id} value={exp.title}>
                            {exp.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="field-hint">Choose the type of travel experience you're looking for</p>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="destination">Destination(s) * <span className="max-hint">(Max 5)</span></Label>
                    <Input
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={(e) => {
                        const value = e.target.value;
                        const destinations = value.split(',').filter(d => d.trim());
                        if (destinations.length <= 5) {
                          handleInputChange(e);
                        }
                      }}
                      required
                      placeholder="e.g., Paris, Rome or Lisbon, Madrid, Barcelona"
                    />
                    <span className="field-hint">For multi-city, separate with commas (max 5 destinations)</span>
                  </div>
                  <div className="form-field">
                    <Label htmlFor="nights_per_destination">Nights per Destination</Label>
                    <Input
                      id="nights_per_destination"
                      name="nights_per_destination"
                      value={formData.nights_per_destination}
                      onChange={handleInputChange}
                      placeholder="e.g., 3, 4, 5 (for multi-city)"
                    />
                    <span className="field-hint">Optional: specify nights for each city</span>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="origin_city">Departing From *</Label>
                    <Input
                      id="origin_city"
                      name="origin_city"
                      value={formData.origin_city}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., New Delhi, Mumbai"
                    />
                  </div>
                  <div className="form-field">
                    <Label>Flight Class</Label>
                    <Select 
                      value={formData.flight_class}
                      onValueChange={(value) => handleSelectChange('flight_class', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="economy">Economy</SelectItem>
                        <SelectItem value="premium-economy">Premium Economy</SelectItem>
                        <SelectItem value="business">Business Class</SelectItem>
                        <SelectItem value="first">First Class</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="travel_start_date">Start Date *</Label>
                    <Input
                      id="travel_start_date"
                      name="travel_start_date"
                      type="date"
                      value={formData.travel_start_date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <Label htmlFor="travel_end_date">End Date *</Label>
                    <Input
                      id="travel_end_date"
                      name="travel_end_date"
                      type="date"
                      value={formData.travel_end_date}
                      onChange={handleInputChange}
                      min={formData.travel_start_date || new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <Label htmlFor="num_travelers">Number of Travelers</Label>
                    <Select 
                      value={formData.num_travelers.toString()}
                      onValueChange={(value) => handleSelectChange('num_travelers', parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Traveler</SelectItem>
                        <SelectItem value="2">2 Travelers</SelectItem>
                        <SelectItem value="3">3 Travelers</SelectItem>
                        <SelectItem value="4">4 Travelers</SelectItem>
                        <SelectItem value="5">5+ Travelers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-field">
                    <Label htmlFor="budget_range">Budget Range (INR)</Label>
                    <Select 
                      value={formData.budget_range}
                      onValueChange={(value) => handleSelectChange('budget_range', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-5L">₹1 - 5 Lakhs</SelectItem>
                        <SelectItem value="5-10L">₹5 - 10 Lakhs</SelectItem>
                        <SelectItem value="10-25L">₹10 - 25 Lakhs</SelectItem>
                        <SelectItem value="25-50L">₹25 - 50 Lakhs</SelectItem>
                        <SelectItem value="50L-1Cr">₹50 Lakhs - 1 Crore</SelectItem>
                        <SelectItem value="1Cr+">₹1 Crore+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="form-section-title">
                  <Heart size={20} />
                  <span>Preferences</span>
                </div>

                <div className="form-row triple">
                  <div className="form-field">
                    <Label>Travel Style</Label>
                    <Select 
                      value={formData.travel_style}
                      onValueChange={(value) => handleSelectChange('travel_style', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="relaxed">Relaxed & Leisurely</SelectItem>
                        <SelectItem value="balanced">Balanced</SelectItem>
                        <SelectItem value="adventure">Adventure & Active</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-field">
                    <Label>Accommodation Type</Label>
                    <Select 
                      value={formData.accommodation_type}
                      onValueChange={(value) => handleSelectChange('accommodation_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="budget">Budget (3 Star)</SelectItem>
                        <SelectItem value="mid-range">Mid-Range (4 Star)</SelectItem>
                        <SelectItem value="luxury">Luxury (5 Star)</SelectItem>
                        <SelectItem value="ultra-luxury">Ultra-Luxury (Palace/Resort)</SelectItem>
                        <SelectItem value="boutique">Boutique Hotels</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-field">
                    <Label>Food Preference</Label>
                    <Select 
                      value={formData.food_preference}
                      onValueChange={(value) => handleSelectChange('food_preference', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="veg">Vegetarian Only</SelectItem>
                        <SelectItem value="non-veg">Non-Vegetarian</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="form-field">
                  <Label>Special Interests (Select all that apply)</Label>
                  <div className="interests-grid">
                    {['Honeymoon', 'Family', 'Photography', 'Wildlife', 'History', 'Adventure Sports', 'Wellness & Spa', 'Fine Dining', 'Local Culture', 'Shopping', 'Nightlife'].map(interest => (
                      <button
                        key={interest}
                        type="button"
                        className={`interest-tag ${formData.special_interests.includes(interest) ? 'selected' : ''}`}
                        onClick={() => handleInterestToggle(interest)}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-field">
                  <Label htmlFor="special_requests">Special Requests</Label>
                  <Textarea
                    id="special_requests"
                    name="special_requests"
                    value={formData.special_requests}
                    onChange={handleInputChange}
                    placeholder="Any specific requirements, celebrations, dietary restrictions..."
                    rows={3}
                  />
                </div>

                <div className="form-actions">
                  <Button type="button" variant="outline" onClick={() => setCurrentStep(verifyLaterMode ? 1 : 2)} className="form-back-btn">
                    Back
                  </Button>
                  <Button type="submit" className="form-next-btn" disabled={loading} data-testid="generate-btn">
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        <span>Crafting Your Journey...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} />
                        Generate My Itinerary
                      </>
                    )}
                  </Button>
                </div>
                {loading && (
                  <p className="generate-note" style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)' }}>
                    This may take 30-60 seconds as our AI curates your personalized journey
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section-above">
        <div className="container">
          <div className="trust-content-inline">
            <p className="trust-quote-inline">
              "Your journey deserves more than a booking confirmation. It deserves <strong>intention</strong>, <strong>elegance</strong>, and <strong>JNT</strong>."
            </p>
            <div className="trust-badge-inline">
              <Star size={16} />
              <span>Limited premium planning slots each month</span>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section - Why Choose JNT */}
      <section className="value-section">
        <div className="container">
          <h2 className="value-heading">Why Choose JNT as Your Travel Partner?</h2>
          <p className="value-subheading">Every client receives a professionally designed, co-branded digital itinerary booklet</p>
          
          <div className="value-grid">
            <ValueCard 
              icon={Award}
              title="Personalized Branding"
              description="Your journey, your name, your custom trip title — curated exclusively by JNT"
            />
            <ValueCard 
              icon={Shield}
              title="Curated, Not Crowded"
              description="Hand-picked experiences tailored to your style, not generic tourist packages"
            />
            <ValueCard 
              icon={Clock}
              title="Strategic Planning"
              description="Optimized logistics, insider recommendations, and hidden gems"
            />
            <ValueCard 
              icon={Globe}
              title="Local Expertise"
              description="VIP upgrades, exclusive access, and stress-free execution"
            />
            <ValueCard 
              icon={Heart}
              title="24/7 Concierge Support"
              description="Dedicated travel expert available throughout your journey for any assistance"
            />
            <ValueCard 
              icon={Star}
              title="Best Price Guarantee"
              description="Competitive pricing with no hidden costs and flexible payment options"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignMyJourney;
